/**
 * Milestone 3 wire shapes (docs/API.md "Milestone 3 additions"): owner home, creator profiles,
 * risk acknowledgement, creator pricing, invoices, discovery and token candles.
 */
import { z } from 'zod'
import { Address, Cursor, DecimalString, Handle, IsoTimestamp, MicroString, PageLimit, UsdString, Uuid, WeiString } from '../primitives.js'
import { AgentStatus, InvoiceKind, InvoiceStatus, PerfRange } from '../enums.js'
import { AgentSummary } from './common.js'
import { AgentCard, BrainId, ModelInterpretation } from './agents.js'
import { AgentTokenCard } from './tokens.js'

// Owner home ------------------------------------------------------------------------

export const MyAgentsQuery = z.object({ status: AgentStatus.optional(), cursor: Cursor.optional(), limit: PageLimit })

/** Something the owner should act on (`code` e.g. budget_exhausted, subscription_lapsed, tick_failed). */
export const AgentIssue = z.object({
  code: z.string().min(1).max(60),
  severity: z.enum(['info', 'warning', 'error']),
  message: z.string(),
  at: IsoTimestamp.nullable(),
})
export type AgentIssue = z.output<typeof AgentIssue>

/** GET /me/agents: the owner's agents with operating status. */
export const MyAgent = AgentCard.extend({
  /** Current strategy version number; null before version 1 exists. */
  version: z.number().int().positive().nullable(),
  /** Credits charged today (UTC) across the agent's envelopes. */
  creditsTodayMicro: MicroString,
  lastTickAt: IsoTimestamp.nullable(),
  openIssues: z.array(AgentIssue),
})
export type MyAgent = z.output<typeof MyAgent>

/** One reward token a holder can claim now (summed over claimable epochs). */
export const ClaimableReward = z.object({
  rewardToken: Address,
  rewardSymbol: z.string().nullable(),
  rewardDecimals: z.number().int().min(0).max(36).nullable(),
  amountWei: WeiString,
  epochs: z.number().int().nonnegative(),
  /**
   * When the oldest of these epochs stops being claimable (lane ALGOBOO1): its close plus the distributor's claim window
   * (`claimWindowEpochs` epochs of `epochLengthBlocks` 12 s blocks); after it the unclaimed amount rolls forward to the
   * holders of the open epoch. Null when the deployment names no window or the epoch's close is not indexed.
   */
  expiresAt: IsoTimestamp.nullable().optional(),
})
export type ClaimableReward = z.output<typeof ClaimableReward>

/** GET /me/holdings: one row per agent token held by any of the caller's linked wallets. */
export const Holding = z.object({
  token: AgentTokenCard,
  /** Sum over linked wallets, base units. */
  balance: WeiString,
  wallets: z.array(z.object({ address: Address, balance: WeiString })),
  quoteSymbol: z.string().nullable(),
  /** balance × pool price, quote units; null without a seeded pool. */
  valueInQuote: DecimalString.nullable(),
  claimable: z.array(ClaimableReward),
  lastClaimAt: IsoTimestamp.nullable(),
  /** When the latest week (distribution epoch) of this token closed; null while none has closed. */
  lastCloseAt: IsoTimestamp.nullable(),
})
export type Holding = z.output<typeof Holding>

// Creator profiles -------------------------------------------------------------------

export const UserCard = z.object({
  id: Uuid,
  handle: Handle,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  primaryAddress: Address,
})
export type UserCard = z.output<typeof UserCard>

/** GET /u/:handle (`social.creator_stats` for the aggregates). */
export const CreatorProfile = z.object({
  user: UserCard,
  agents: z.array(AgentCard),
  tokens: z.array(AgentTokenCard),
  stats: z.object({
    agentsCount: z.number().int().nonnegative(),
    followersTotal: z.number().int().nonnegative(),
    /** Null until the platform serves strategy performance (lane BOO2). */
    pnlAllUsd: UsdString.nullable(),
    holdersTotal: z.number().int().nonnegative(),
    updatedAt: IsoTimestamp.nullable(),
  }),
  joinedAt: IsoTimestamp,
})
export type CreatorProfile = z.output<typeof CreatorProfile>

// Cost estimate ----------------------------------------------------------------------

export const CostEstimateQuery = z.object({
  ticksPerDay: z.coerce.number().int().min(1).max(1440).default(144),
  mode: ModelInterpretation.default('never'),
})

/** GET /brains/:id/estimate from the brain's current rate card. */
export const CostEstimate = z.object({
  brainId: BrainId,
  mode: ModelInterpretation,
  ticksPerDay: z.number().int().positive(),
  rateCardId: Uuid.nullable(),
  inputMicroPer1k: MicroString,
  outputMicroPer1k: MicroString,
  markupBps: z.number().int().nonnegative(),
  assumptions: z.object({
    callsPerDay: z.number().nonnegative(),
    inputTokensPerCall: z.number().int().nonnegative(),
    outputTokensPerCall: z.number().int().nonnegative(),
  }),
  microPerDay: MicroString,
  microPerMonth: MicroString,
  usdPerMonth: UsdString,
})
export type CostEstimate = z.output<typeof CostEstimate>

// Risk -------------------------------------------------------------------------------------

/** GET /risk-disclosure: the current version the user acknowledges. */
export const RiskDisclosure = z.object({
  version: z.string().min(1).max(40),
  title: z.string(),
  /** Markdown. */
  text: z.string(),
  effectiveAt: IsoTimestamp,
})
export type RiskDisclosure = z.output<typeof RiskDisclosure>

export const AcknowledgeRisk = z.object({ version: z.string().min(1).max(40) })
export type AcknowledgeRisk = z.input<typeof AcknowledgeRisk>

/** `trading.risk_acknowledgements` row as returned by POST /me/risk-acknowledgement. */
export const RiskAcknowledgement = z.object({ version: z.string(), acknowledgedAt: IsoTimestamp })
export type RiskAcknowledgement = z.output<typeof RiskAcknowledgement>

// Creator pricing and invoices -------------------------------------------------------

/** `agents.agent_pricing` row; tier ids and price bounds are `AGENT_PRICING_BOUNDS` (catalog). */
export const PricingTier = z.object({
  tier: z.string().min(1).max(40),
  priceUsdMonth: UsdString,
  description: z.string(),
  features: z.array(z.string()),
  sort: z.number().int(),
  enabled: z.boolean(),
})
export type PricingTier = z.output<typeof PricingTier>

export const PricingTierInput = z.object({
  tier: z.string().min(1).max(40),
  priceUsdMonth: UsdString,
  description: z.string().max(280).default(''),
  features: z.array(z.string().min(1).max(120)).max(8).default([]),
  sort: z.number().int().optional(),
  enabled: z.boolean().default(true),
})
/** PATCH /agents/:handle/pricing: replaces the agent's tiers. */
export const SetAgentPricing = z.object({ tiers: z.array(PricingTierInput).min(1).max(3) })
export type SetAgentPricing = z.input<typeof SetAgentPricing>

/** `billing.invoices`: performance fees and renewals owed by the user. */
export const Invoice = z.object({
  id: Uuid,
  kind: InvoiceKind,
  status: InvoiceStatus,
  agent: AgentSummary.nullable(),
  /** `accounts.wallets.id` a performance fee was earned on, if any. */
  walletId: Uuid.nullable(),
  amountUsd: UsdString,
  dueAt: IsoTimestamp,
  paymentIntentId: Uuid.nullable(),
  createdAt: IsoTimestamp,
  paidAt: IsoTimestamp.nullable(),
})
export type Invoice = z.output<typeof Invoice>

export const InvoicesQuery = z.object({ status: InvoiceStatus.optional() })

// Discovery and sharing --------------------------------------------------------------

export const TrendingQuery = z.object({ range: PerfRange.default('24h') })

/** GET /trending: agents and tokens ranked by recent activity and volume (`score`, higher first). */
export const TrendingItem = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('agent'),
    score: DecimalString,
    agent: AgentCard,
    followsDelta: z.number().int(),
    tradesDelta: z.number().int(),
    holdersDelta: z.number().int(),
  }),
  z.object({
    type: z.literal('token'),
    score: DecimalString,
    token: AgentTokenCard,
    volumeUsd: UsdString.nullable(),
    holdersDelta: z.number().int(),
  }),
])
export type TrendingItem = z.output<typeof TrendingItem>

/** GET /agents/:handle/og: what the Pages Function puts into Open Graph / Twitter tags. */
export const OgData = z.object({
  handle: Handle,
  title: z.string(),
  description: z.string(),
  /** Absolute URL of the share card image; null falls back to the site default. */
  imageUrl: z.string().nullable(),
  /** Canonical page URL. */
  url: z.string(),
  agent: AgentSummary,
  pnl7dUsd: UsdString.nullable(),
  pnlAllUsd: UsdString.nullable(),
  token: z.object({ address: Address.nullable(), symbol: z.string(), priceInQuote: DecimalString.nullable(), quoteSymbol: z.string().nullable() }).nullable(),
})
export type OgData = z.output<typeof OgData>

// Token candles ----------------------------------------------------------------------

export const CandleInterval = z.enum(['5m', '15m', '1h', '4h', '1d'])
export type CandleInterval = z.output<typeof CandleInterval>

export const CandlesQuery = z.object({
  interval: CandleInterval.default('1h'),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
})

export const Candle = z.object({
  t: IsoTimestamp,
  o: DecimalString,
  h: DecimalString,
  l: DecimalString,
  c: DecimalString,
  /** Traded volume in quote units. */
  volumeQuote: DecimalString,
})
export type Candle = z.output<typeof Candle>

/** GET /tokens/:address/candles: prices in quote units per agent token, oldest first. */
export const TokenCandles = z.object({
  token: Address,
  interval: CandleInterval,
  quoteAsset: Address.nullable(),
  quoteSymbol: z.string().nullable(),
  candles: z.array(Candle),
  /**
   * false when market data is not connected or unreachable (the read answers 200 with no candles and `reason`,
   * never invented bars); true otherwise, including a token without a pool (an empty list).
   */
  available: z.boolean(),
  reason: z.string().nullable(),
})
export type TokenCandles = z.output<typeof TokenCandles>
